/* global gtag */
import React, { useState, useEffect, useRef } from 'react';
import useAuditLogger from '../utils/auditHelpers';

// This hook handles tracking impressions and clicks.
const usePartnerTracking = (logAudit) => {
  const trackImpression = async (partnerId, partnerName) => {
    if (!logAudit) return;
    try {
      await logAudit('partner_impression', { partnerId, partnerName });
    } catch (error) {
      console.error('Audit Error: Could not log partner impression.', error);
    }
    if (typeof gtag !== 'undefined') {
      gtag('event', 'partner_impression', { partner_id: partnerId, partner_name: partnerName });
    }
  };

  const trackClick = async (partnerId, partnerName, url) => {
    if (!logAudit) return;
    try {
      await logAudit('partner_click', { partnerId, partnerName, destinationUrl: url });
    } catch (error) {
      console.error('Audit Error: Could not log partner click.', error);
    }
    if (typeof gtag !== 'undefined') {
      gtag('event', 'partner_click', { partner_id: partnerId, partner_name: partnerName, partner_url: url });
    }
  };

  return { trackImpression, trackClick };
};

// This component displays a single partner logo.
// ✅ CHANGED: Adjusted width and added flex-shrink-0 for marquee
const PartnerCard = ({ partner, onImpression, onClick }) => {
  const cardRef = useRef(null);
  const wasImpressed = useRef(false);

  useEffect(() => {
    if (!cardRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !wasImpressed.current) {
          wasImpressed.current = true;
          onImpression(partner.id, partner.name);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [partner.id, partner.name, onImpression]);

  const handleClick = (e) => {
    e.preventDefault();
    onClick(partner.id, partner.name, partner.url);
    setTimeout(() => {
      window.open(partner.url, '_blank', 'noopener,noreferrer');
    }, 150);
  };

  return (
    // ✅ CHANGED: Set a fixed width for consistent spacing in the marquee
    <div ref={cardRef} className="flex-shrink-0 w-36 h-20 flex items-center justify-center p-3 mx-4">
      <div
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(e); }}
        aria-label={`Visitar ${partner.name}`}
        className="group w-full h-full flex items-center justify-center rounded-lg hover:bg-white hover:shadow-lg transition-all duration-300 cursor-pointer"
      >
        <img
          src={partner.logo}
          alt={partner.name}
          className="max-h-12 max-w-full object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300"
          loading="lazy"
        />
      </div>
    </div>
  );
};

// This is the main component, now with marquee logic.
const PartnersSection = ({
  db,
  user,
  partners = [],
  title = "Nossos Parceiros",
  auditEnabled = true
}) => {
  const { logAudit } = useAuditLogger(db, user);
  const { trackImpression, trackClick } = usePartnerTracking(auditEnabled ? logAudit : null);
  const [activePartners, setActivePartners] = useState([]);

  // Filtering logic remains the same
  useEffect(() => {
    const today = new Date();
    const filtered = partners
      .filter(partner => {
        if (partner.active !== true) return false;
        if (partner.campaign && partner.campaign.startDate && partner.campaign.endDate) {
          const start = new Date(partner.campaign.startDate + 'T00:00:00');
          const end = new Date(partner.campaign.endDate + 'T23:59:59');
          if (isNaN(start.getTime()) || isNaN(end.getTime())) return true;
          return today >= start && today <= end;
        }
        return true;
      })
      .sort((a, b) => (a.priority || 99) - (b.priority || 99));
    setActivePartners(filtered);
  }, [partners]);

  // ❌ REMOVED: All old carousel state and effects (currentIndex, itemsPerView, etc.)

  if (!activePartners.length) return null;

  // ✅ CHANGED: Calculate animation duration based on the number of partners for consistent speed
  const animationDuration = `${activePartners.length * 5}s`;

  return (
    <div className="bg-gray-50 border-t border-b border-gray-200">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h2 className="text-center text-base font-semibold text-gray-700 mb-6">
          {title}
        </h2>

        {/* ✅ CHANGED: This is the new marquee structure */}
        <div className="relative flex overflow-hidden group">
          <div 
            className="flex animate-marquee group-hover:[animation-play-state:paused]"
            style={{ animationDuration }}
          >
            {/* Render the list of partners */}
            {activePartners.map((partner) => (
              <PartnerCard
                key={partner.id}
                partner={partner}
                onImpression={trackImpression}
                onClick={trackClick}
              />
            ))}
            {/* ✅ ADDED: Render the list AGAIN to create a seamless loop */}
            {activePartners.map((partner) => (
              <PartnerCard
                key={`${partner.id}-clone`}
                partner={partner}
                onImpression={() => {}} // Don't track impressions for clones
                onClick={trackClick}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnersSection;
